import { BrowserRouter, HashRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './features/authentication/contexts/AuthContext';
import { AvatarProvider } from './features/profile/contexts/AvatarContext';
import { GoalModalProvider } from './features/profile/contexts/GoalModalContext';
import { GoalModals } from './features/profile/components/GoalModals';
import { ThemeProvider } from './context/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import MainLayout from './components/MainLayout';
import { setupAndroidBackButton } from './utils/androidBackButton';
import { NotificationManager } from './components/NotificationManager';
import { analyticsService } from './services/analyticsService';
import { getValidAccessToken } from './services/authService';

import { LoadingPage } from './features/core/pages';
import { DashboardPage } from './features/dashboard/pages';
import { LibraryRoutes } from './features/library/routes';
import SavedBooksPage from './features/library/pages/SavedBooksPage';
import DailyQuests from './features/learning/pages/DailyQuests';
import ChallengesPage from './features/learning/pages/ChallengesPage';
import StreakPage from './features/learning/pages/StreakPage';
import StreakCalendarPage from './features/learning/pages/StreakCalendarPage';
import XPClaimPage from './features/learning/pages/XPClaimPage';
import GoalsRewardPage from './features/learning/pages/GoalsRewardPage';
import LearningPath from './features/learning/pages/LearningPath';
import AchievementPage from './features/learning/pages/AchievementPage';
import { LeaguePage } from './features/league/pages';
import { SettingsPage } from './features/settings/pages';
import { ProfilePage, ReferralPage } from './features/profile/pages';
import PaymentHistory from './features/payments/pages/PaymentHistory';
import PremiumSubscription from './features/subscription/pages/PremiumSubscription';
import PaymentConfirmation from './features/subscription/pages/PaymentConfirmation';
import GemsPage from './features/gems/pages/GemsPage';
import TipsPage from './features/shorts/pages/TipsPage';
import AuthorBooksPage from './features/library/pages/AuthorBooksPage';
import AchievementsPage from './features/achievements/pages/Achievements';
import TagBooksPage from './features/tags/pages/TagBooksPage';
import ReportHistory from './features/reports/pages/ReportHistory';
import { BookReadingPage, NovelReadingPage } from './features/reading/pages';
import ChapterReview from './features/chapters/pages/ChapterReview';
import ReviewResult from './features/chapters/pages/ReviewResult';
import { isNativeStoreBuild, restoreAndVerifyStorePurchases } from './services/inAppBilling';
import { useAuth } from './features/authentication/contexts/AuthContext';
import {
  WelcomePage,
  LoginPage,
  OTPVerificationPage,
  OnboardingTopicsPage,
  OnboardingBooksPage,
  OnboardingPhonePage,
  OnboardingOTPVerificationPage,
  OnboardingNamePage
} from './features/authentication/pages';
import OnboardingLayout from './features/authentication/components/OnboardingLayout';
import { getOnboardingResumePath } from './utils/onboardingProgress';
import { CapacitorUtils } from './utils/capacitor';

let __protectedRouteMinDelayShown = false;

const SCREEN_NAMES: Record<string, string> = {
  '/': 'root',
  '/dashboard': 'dashboard',
  '/library': 'library',
  '/library/book': 'book_detail',
  '/learning/path': 'learning_path',
  '/read': 'book_reading',
  '/novel-read': 'novel_reading',
  '/premium-subscription': 'subscription_plans',
  '/payment-confirmation': 'payment_confirmation',
  '/gems': 'gems_shop',
  '/tips': 'tips',
  '/profile': 'profile',
  '/settings': 'settings',
};

const AnalyticsScreenTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const basePath = location.pathname.replace(/\/$/, '') || '/';
    const screenName = SCREEN_NAMES[basePath]
      || basePath.replace(/^\//, '').replace(/[/:]+/g, '_')
      || 'unknown';
    void analyticsService.setScreenName(screenName);
  }, [location.pathname]);

  return null;
};

// React Router does not reset the browser scroll position between routes.
// Use the location key so this also works when navigation keeps the same path
// but changes location state (for example, switching between book details).
const ScrollToTop: React.FC = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.key]);

  return null;
};

const AnalyticsUserIdentitySync: React.FC = () => {
  const { user, isLoading } = useAuth();

  useEffect(() => {
    if (isLoading) return;

    let cancelled = false;
    const sync = async () => {
      if (!user?.is_registered) {
        await analyticsService.setUserId(null);
        return;
      }
      const accessToken = await getValidAccessToken();
      if (!cancelled) await analyticsService.setUserIdFromAccessToken(accessToken);
    };
    void sync();
    return () => { cancelled = true; };
  }, [isLoading, user?.is_registered, user?.id]);

  return null;
};


const queryClient = new QueryClient();

const RewardAnimationPreloader = () => (
  <video
    src="/reward_animation.mp4"
    preload="auto"
    muted
    playsInline
    aria-hidden="true"
    className="pointer-events-none fixed h-px w-px opacity-0"
  />
);
console.log('🎯 [App] App component loading...');
// Component to setup Android back button (must be inside Router)
const AndroidBackButtonSetup: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setupAndroidBackButton(navigate);
  }, [navigate]);

  return null;
};

// Component to block back navigation to auth pages after login
// این component فقط جلوی برگشت به صفحات auth/onboarding را می‌گیرد
// و اجازه می‌دهد که در صفحات داخلی برنامه back navigation کار کند
const BlockAuthBackNavigation: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;

    // List of auth/onboarding routes that user shouldn't go back to after login
    const authRoutes = [
      '/welcome',
      '/login',
      '/otp-verification',
      '/onboarding/topics',
      '/onboarding/books',
      '/onboarding/read',
      '/onboarding/novel-read',
      '/onboarding/xp-claim',
      '/onboarding/streak',
      '/onboarding/achievement',
      '/onboarding/phone',
      '/onboarding/otp-verification',
      '/onboarding/name'
    ];

    // Check if current route is NOT an auth route (user is in app)
    const isInApp = !authRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/'));

    // Only block back navigation for fully registered users in app pages
    const isFullyRegistered = user && !user.is_guest && user.is_registered;

    if (isInApp && isFullyRegistered) {
      // این listener فقط زمانی فعال می‌شود که کاربر سعی کند به صفحات auth برگردد
      // برای navigation عادی در صفحات داخلی مشکلی ایجاد نمی‌کند
      const handlePopState = () => {
        // بررسی می‌کنیم که آیا کاربر دارد به صفحه auth برمی‌گردد
        const currentPath = window.location.pathname;
        const isGoingToAuth = authRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'));
        
        if (isGoingToAuth) {
          // اگر دارد به auth برمی‌گردد، جلویش را بگیر و به dashboard برگردان
          console.log('🚫 [BlockAuthBackNavigation] Blocked back navigation to auth pages');
          navigate('/dashboard', { replace: true });
        }
        // در غیر این صورت اجازه back navigation عادی را بده
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isAuthenticated, user, location.pathname, navigate]);

  return null;
};

// Main application component
function App() {
  console.log('🏗️ [App] App function executing...');
  // Determine platform once
  const isNative = (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.());

  // Expose a light recheck function for auth flows to await token readiness
  useEffect(() => {
    (window as any).recheckAuth = async () => {
      try {
        await getValidAccessToken();
      } catch { }
    };

    return () => {
      try { delete (window as any).recheckAuth; } catch { }
    };
  }, []);

  // Root redirect component - sends users based on their auth status
  const RootRedirect: React.FC = () => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const [pendingPaymentRoute, setPendingPaymentRoute] = useState<string | null>(() => {
      try {
        return window.localStorage.getItem('booka_pending_payment_route');
      } catch {
        return null;
      }
    });
    const [pendingNotificationRoute, setPendingNotificationRoute] = useState<{
      route: string;
      routeStateRaw?: string | null;
    } | null>(() => {
      try {
        const value = window.localStorage.getItem('booka_pending_notification_route');
        if (!value) return null;
        const parsed = JSON.parse(value);
        return typeof parsed?.route === 'string' ? parsed : null;
      } catch {
        return null;
      }
    });

    useEffect(() => {
      if (!isAuthenticated || !user || user.is_guest || !user.is_registered || !isNativeStoreBuild()) return;
      let alreadyStarted = false;
      try { alreadyStarted = sessionStorage.getItem('booka_store_recovery_started') === '1'; } catch {}
      if (alreadyStarted) return;
      try { sessionStorage.setItem('booka_store_recovery_started', '1'); } catch {}

      void restoreAndVerifyStorePurchases().catch((error) => {
        console.warn('Store purchase recovery failed; it will be retried in a new session', error);
        try { sessionStorage.removeItem('booka_store_recovery_started'); } catch {}
      });
    }, [isAuthenticated, user]);

    useEffect(() => {
      const onPendingPaymentRoute = (event: Event) => {
        const route = (event as CustomEvent<string>).detail;
        if (typeof route === 'string' && route.startsWith('/payment-confirmation?')) {
          setPendingPaymentRoute(route);
        }
      };

      const onPendingNotificationRoute = (event: Event) => {
        const detail = (event as CustomEvent<{ route?: string; routeStateRaw?: string | null }>).detail;
        if (typeof detail?.route === 'string') {
          setPendingNotificationRoute({
            route: detail.route,
            routeStateRaw: detail.routeStateRaw,
          });
        }
      };

      window.addEventListener('booka:pending-payment-route', onPendingPaymentRoute);
      window.addEventListener('booka:pending-notification-route', onPendingNotificationRoute);
      const syncPendingRoutes = () => {
        try {
          const paymentRoute = window.localStorage.getItem('booka_pending_payment_route');
          if (paymentRoute) setPendingPaymentRoute(paymentRoute);

          const notificationValue = window.localStorage.getItem('booka_pending_notification_route');
          if (notificationValue) {
            const parsed = JSON.parse(notificationValue);
            if (typeof parsed?.route === 'string') setPendingNotificationRoute(parsed);
          }
        } catch {
          // Ignore malformed or unavailable storage.
        }
      };
      const pendingRouteSyncTimer = window.setInterval(syncPendingRoutes, 100);
      return () => {
        window.removeEventListener('booka:pending-payment-route', onPendingPaymentRoute);
        window.removeEventListener('booka:pending-notification-route', onPendingNotificationRoute);
        window.clearInterval(pendingRouteSyncTimer);
      };
    }, []);

    // A payment callback must win over the normal authenticated-user redirect
    // to dashboard. Keep the root route suspended until auth is ready, then
    // hand the callback route to PaymentConfirmation.
    if (pendingPaymentRoute) {
      if (isAuthenticated && user && !user.is_guest && user.is_registered) {
        return <Navigate to={pendingPaymentRoute} replace />;
      }
      if (isAuthenticated && !user) return null;
    }

    if (pendingNotificationRoute && isAuthenticated && user && !user.is_guest && user.is_registered) {
      let state: Record<string, any> | undefined;
      if (pendingNotificationRoute.routeStateRaw) {
        try {
          state = JSON.parse(pendingNotificationRoute.routeStateRaw);
        } catch {
          state = undefined;
        }
      }
      return <Navigate to={pendingNotificationRoute.route} state={state} replace />;
    }
    if (pendingNotificationRoute && isAuthenticated && !user) return null;

    if (!isAuthenticated) return <Navigate to="/welcome" replace />;
    if (user?.is_guest || !user?.is_registered) return <Navigate to={getOnboardingResumePath()} replace />;
    return <Navigate to="/dashboard" replace />;
  };

  // Guard component for protected routes - requires full registration
  const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const [minDelayPassed, setMinDelayPassed] = useState(true);
    const [suppressed, setSuppressed] = useState(false);

    // Minimum delay only on very first mount to avoid flicker on route changes
    useEffect(() => {
      if (!__protectedRouteMinDelayShown) {
        const timer = setTimeout(() => {
          __protectedRouteMinDelayShown = true;
          setMinDelayPassed(true);
        }, 800);
        return () => clearTimeout(timer);
      } else {
        setMinDelayPassed(true);
      }
    }, []);

    // React to suppression flag changes via custom event
    useEffect(() => {
      const onSuppressEnd = () => setSuppressed(false);
      window.addEventListener('auth:suppress-end', onSuppressEnd as EventListener);
      // Fallback timer in case event doesn't fire
      let fallback: number | undefined;
      try {
        if ((window as any).__suppressAuthRedirect === true) {
          // auto-clear after 4s just in case
          fallback = window.setTimeout(() => setSuppressed(false), 4000);
        }
      } catch { }
      return () => {
        window.removeEventListener('auth:suppress-end', onSuppressEnd as EventListener);
        if (fallback) window.clearTimeout(fallback);
      };
    }, []);

    // Show loading while context is loading, minimum delay not passed, or suppression active
    if (isLoading || !minDelayPassed || suppressed) {
      console.log('⏳ [ProtectedRoute] Loading...', { isLoading, minDelayPassed, suppressed });
      return null;
    }

    // Not authenticated -> Login
    if (!isAuthenticated) {
      console.log('🚫 [ProtectedRoute] Not authenticated, redirecting to login');
      return <Navigate to="/login" replace />;
    }
    
    // Log user state for debugging
    console.log('🔍 [ProtectedRoute] User state:', {
      is_guest: user?.is_guest,
      is_registered: user?.is_registered,
      name: user?.name,
      phone: user?.phone_number
    });
    
    // Guest user or not registered -> Complete onboarding first
    if (user?.is_guest || !user?.is_registered) {
      console.log('🚫 [ProtectedRoute] User is guest or not registered, redirecting to onboarding');
      return <Navigate to={getOnboardingResumePath()} replace />;
    }
    
    console.log('✅ [ProtectedRoute] User is fully registered, allowing access');
    return children;
  };

  // Guard component for onboarding routes - requires token but allows guest/unregistered users
  const GuestRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const [checking, setChecking] = useState(false);

    useEffect(() => {
      let mounted = true;
      const checkAuth = async () => {
        try {
          const token = await getValidAccessToken();
          if (!mounted) return;
          
          if (!token) {
            // No token at all -> redirect to welcome
            console.log('🚫 [GuestRoute] No token, redirecting to welcome');
            setChecking(false);
            return;
          }

          // Token exists, wait for AuthContext to load user profile
          if (!isLoading) {
            setChecking(false);
          }
        } catch (e) {
          if (!mounted) return;
          console.log('🚫 [GuestRoute] Token check failed, redirecting to welcome');
          setChecking(false);
        }
      };

      checkAuth();
      return () => { mounted = false; };
    }, [isLoading]);

    // Show loading while checking
    if (checking || isLoading) return children;

    // No authentication at all -> Welcome
    if (!isAuthenticated) {
      console.log('🚫 [GuestRoute] Not authenticated, redirecting to welcome');
      return <Navigate to="/welcome" replace />;
    }

    // Fully registered user trying to access onboarding -> Dashboard
    if (user && !user.is_guest && user.is_registered) {
      console.log('✅ [GuestRoute] User already registered, redirecting to dashboard');
      return <Navigate to="/dashboard" replace />;
    }

    // Guest or unregistered user -> Allow access to onboarding
    return children;
  };

  // Single full-page loading boundary for auth/bootstrap. Route guards only
  // decide redirects; they never render the global loading screen.
  const CentralLoadingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isLoading } = useAuth();
    const [phase, setPhase] = useState<'loading' | 'exiting' | 'ready'>(
      isLoading ? 'loading' : 'ready'
    );

    const hideSplashAfterReady = () => {
      void CapacitorUtils.hideSplashScreen();
    };

    useEffect(() => {
      if (isLoading) {
        setPhase('loading');
      } else if (phase === 'loading') {
        setPhase('exiting');
      }
    }, [isLoading, phase]);

    // If auth was already restored before the gate mounted, there is no
    // LoadingPage animation to wait for. Release after the first app-shell
    // render so Welcome/Dashboard never remain behind the native splash.
    useEffect(() => {
      if (phase === 'ready' && !isLoading) {
        const frame = window.requestAnimationFrame(hideSplashAfterReady);
        return () => window.cancelAnimationFrame(frame);
      }
    }, [isLoading, phase]);

    if (phase !== 'ready') {
      return (
        <LoadingPage
          isExiting={phase === 'exiting'}
          onLoadingAnimationReady={hideSplashAfterReady}
          onExitComplete={() => setPhase('ready')}
        />
      );
    }

    return <>{children}</>;
  };

  // Wrapper for NotificationManager to get userId from AuthContext
  const NotificationManagerWrapper: React.FC = () => {
    const { user, isAuthenticated, isLoading } = useAuth();

    if (!isNative) {
      return null;
    }

    const canOpenProtectedRoutes = !isLoading && isAuthenticated && !user?.is_guest && !!user?.is_registered;

    // Keep the manager mounted during auth/bootstrap. This is required for cold-start
    // notification deep links; the manager queues protected routes until the session
    // is ready instead of sending the user through Welcome first.
    return (
      <NotificationManager
        canOpenProtectedRoutes={canOpenProtectedRoutes}
        canRegisterDevice={canOpenProtectedRoutes}
      />
    );
  };


  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AvatarProvider>
            <GoalModalProvider>
              <ThemeProvider>
              <RewardAnimationPreloader />
              {isNative ? (
                <HashRouter>
                  <AnalyticsScreenTracker />
                  <ScrollToTop />
                  <AnalyticsUserIdentitySync />
                  <AndroidBackButtonSetup />
                  <BlockAuthBackNavigation />
                  <NotificationManagerWrapper />
                  <GoalModals />
                  <CentralLoadingGate>
                  <div className="App">
                    <Routes>
                      <Route path="/" element={<RootRedirect />} />
                      
                      {/* Authentication Routes */}
                      <Route path="/welcome" element={<WelcomePage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/otp-verification" element={<OTPVerificationPage />} />
                      
                      {/* Onboarding Routes */}
                      <Route path="/onboarding" element={<OnboardingLayout />}>
                        <Route index element={<Navigate to="/onboarding/topics" replace />} />
                        <Route path="topics" element={<GuestRoute><OnboardingTopicsPage /></GuestRoute>} />
                        <Route path="books" element={<GuestRoute><OnboardingBooksPage /></GuestRoute>} />
                        <Route path="phone" element={<GuestRoute><OnboardingPhonePage /></GuestRoute>} />
                        <Route path="otp-verification" element={<GuestRoute><OnboardingOTPVerificationPage /></GuestRoute>} />
                        <Route path="name" element={<GuestRoute><OnboardingNamePage /></GuestRoute>} />
                         <Route path="discovery" element={<Navigate to="/onboarding/name" replace />} />
                        <Route path="read" element={<GuestRoute><BookReadingPage /></GuestRoute>} />
                        <Route path="novel-read" element={<GuestRoute><NovelReadingPage /></GuestRoute>} />
                        <Route path="xp-claim" element={<GuestRoute><XPClaimPage /></GuestRoute>} />
                        <Route path="streak" element={<GuestRoute><StreakPage /></GuestRoute>} />
                        <Route path="achievement" element={<GuestRoute><AchievementPage /></GuestRoute>} />
                      </Route>

                      {/* Main App Routes */}
                      <Route path="/dashboard" element={<ProtectedRoute><MainLayout><DashboardPage /></MainLayout></ProtectedRoute>} />
                      <Route path="/library/*" element={<ProtectedRoute><LibraryRoutes /></ProtectedRoute>} />
                      <Route path="/quests" element={<ProtectedRoute><MainLayout><DailyQuests /></MainLayout></ProtectedRoute>} />
                      <Route path="/learning/quests" element={<ProtectedRoute><MainLayout><DailyQuests /></MainLayout></ProtectedRoute>} />
                      <Route path="/learning/challenges" element={<ProtectedRoute><MainLayout><ChallengesPage /></MainLayout></ProtectedRoute>} />
                      <Route path="/learning/streak" element={<ProtectedRoute><StreakPage /></ProtectedRoute>} />
                      <Route path="/learning/streak-calendar" element={<ProtectedRoute><StreakCalendarPage /></ProtectedRoute>} />
                      <Route path="/learning/xp-claim" element={<ProtectedRoute><XPClaimPage /></ProtectedRoute>} />
                      <Route path="/learning/goals-reward" element={<ProtectedRoute><GoalsRewardPage /></ProtectedRoute>} />
                      <Route path="/learning/achievement" element={<ProtectedRoute><AchievementPage /></ProtectedRoute>} />
                      <Route path="/learning/path" element={<ProtectedRoute><MainLayout><LearningPath /></MainLayout></ProtectedRoute>} />
                      <Route path="/saved-books" element={<ProtectedRoute><MainLayout><SavedBooksPage /></MainLayout></ProtectedRoute>} />
                      <Route path="/league" element={<ProtectedRoute><MainLayout><LeaguePage /></MainLayout></ProtectedRoute>} />
                      <Route path="/profile" element={<ProtectedRoute><MainLayout><ProfilePage /></MainLayout></ProtectedRoute>} />
                      <Route path="/referral" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
                      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                      <Route path="/payment-history" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
                      <Route path="/gems" element={<ProtectedRoute><GemsPage /></ProtectedRoute>} />
                      <Route path="/tips" element={<ProtectedRoute><MainLayout><TipsPage /></MainLayout></ProtectedRoute>} />
                      <Route path="/report-history" element={<ProtectedRoute><ReportHistory /></ProtectedRoute>} />
                      <Route path="/premium-subscription" element={<ProtectedRoute><PremiumSubscription /></ProtectedRoute>} />
                      <Route path="/payment-confirmation" element={<ProtectedRoute><PaymentConfirmation /></ProtectedRoute>} />
                      <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
                      <Route path="/author/:author" element={<ProtectedRoute><AuthorBooksPage /></ProtectedRoute>} />
                      <Route path="/tag/:tagId" element={<ProtectedRoute><TagBooksPage /></ProtectedRoute>} />
                      <Route path="/read" element={<ProtectedRoute><BookReadingPage /></ProtectedRoute>} />
                      <Route path="/novel-read" element={<ProtectedRoute><NovelReadingPage /></ProtectedRoute>} />
                      <Route path="/review" element={<ProtectedRoute><ChapterReview /></ProtectedRoute>} />
                      <Route path="/review/result" element={<ProtectedRoute><ReviewResult /></ProtectedRoute>} />
                    </Routes>
                  </div>
                  </CentralLoadingGate>
                </HashRouter>
              ) : (
                <BrowserRouter>
                  <AnalyticsScreenTracker />
                  <ScrollToTop />
                  <AnalyticsUserIdentitySync />
                  <BlockAuthBackNavigation />
                  <GoalModals />
                  <CentralLoadingGate>
                  <div className="App">
                    <Routes>
                      <Route path="/" element={<RootRedirect />} />
                      
                      {/* Authentication Routes */}
                      <Route path="/welcome" element={<WelcomePage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/otp-verification" element={<OTPVerificationPage />} />
                      
                      {/* Onboarding Routes */}
                      <Route path="/onboarding" element={<OnboardingLayout />}>
                        <Route index element={<Navigate to="/onboarding/topics" replace />} />
                        <Route path="topics" element={<GuestRoute><OnboardingTopicsPage /></GuestRoute>} />
                        <Route path="books" element={<GuestRoute><OnboardingBooksPage /></GuestRoute>} />
                        <Route path="phone" element={<GuestRoute><OnboardingPhonePage /></GuestRoute>} />
                        <Route path="otp-verification" element={<GuestRoute><OnboardingOTPVerificationPage /></GuestRoute>} />
                        <Route path="name" element={<GuestRoute><OnboardingNamePage /></GuestRoute>} />
                         <Route path="discovery" element={<Navigate to="/onboarding/name" replace />} />
                        <Route path="read" element={<GuestRoute><BookReadingPage /></GuestRoute>} />
                        <Route path="novel-read" element={<GuestRoute><NovelReadingPage /></GuestRoute>} />
                        <Route path="xp-claim" element={<GuestRoute><XPClaimPage /></GuestRoute>} />
                        <Route path="streak" element={<GuestRoute><StreakPage /></GuestRoute>} />
                        <Route path="achievement" element={<GuestRoute><AchievementPage /></GuestRoute>} />
                      </Route>

                      {/* Main App Routes */}
                      <Route path="/dashboard" element={<ProtectedRoute><MainLayout><DashboardPage /></MainLayout></ProtectedRoute>} />
                      <Route path="/library/*" element={<ProtectedRoute><LibraryRoutes /></ProtectedRoute>} />
                      <Route path="/quests" element={<ProtectedRoute><MainLayout><DailyQuests /></MainLayout></ProtectedRoute>} />
                      <Route path="/learning/quests" element={<ProtectedRoute><MainLayout><DailyQuests /></MainLayout></ProtectedRoute>} />
                      <Route path="/learning/challenges" element={<ProtectedRoute><MainLayout><ChallengesPage /></MainLayout></ProtectedRoute>} />
                      <Route path="/learning/streak" element={<ProtectedRoute><StreakPage /></ProtectedRoute>} />
                      <Route path="/learning/streak-calendar" element={<ProtectedRoute><StreakCalendarPage /></ProtectedRoute>} />
                      <Route path="/learning/xp-claim" element={<ProtectedRoute><XPClaimPage /></ProtectedRoute>} />
                      <Route path="/learning/goals-reward" element={<ProtectedRoute><GoalsRewardPage /></ProtectedRoute>} />
                      <Route path="/learning/achievement" element={<ProtectedRoute><AchievementPage /></ProtectedRoute>} />
                      <Route path="/learning/path" element={<ProtectedRoute><MainLayout><LearningPath /></MainLayout></ProtectedRoute>} />
                      <Route path="/saved-books" element={<ProtectedRoute><MainLayout><SavedBooksPage /></MainLayout></ProtectedRoute>} />
                      <Route path="/league" element={<ProtectedRoute><MainLayout><LeaguePage /></MainLayout></ProtectedRoute>} />
                      <Route path="/profile" element={<ProtectedRoute><MainLayout><ProfilePage /></MainLayout></ProtectedRoute>} />
                      <Route path="/referral" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
                      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                      <Route path="/payment-history" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
                      <Route path="/gems" element={<ProtectedRoute><GemsPage /></ProtectedRoute>} />
                      <Route path="/tips" element={<ProtectedRoute><MainLayout><TipsPage /></MainLayout></ProtectedRoute>} />
                      <Route path="/report-history" element={<ProtectedRoute><ReportHistory /></ProtectedRoute>} />
                      <Route path="/premium-subscription" element={<ProtectedRoute><PremiumSubscription /></ProtectedRoute>} />
                      <Route path="/payment-confirmation" element={<ProtectedRoute><PaymentConfirmation /></ProtectedRoute>} />
                      <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
                      <Route path="/author/:author" element={<ProtectedRoute><AuthorBooksPage /></ProtectedRoute>} />
                      <Route path="/tag/:tagId" element={<ProtectedRoute><TagBooksPage /></ProtectedRoute>} />
                      <Route path="/read" element={<ProtectedRoute><BookReadingPage /></ProtectedRoute>} />
                      <Route path="/novel-read" element={<ProtectedRoute><NovelReadingPage /></ProtectedRoute>} />
                      <Route path="/review" element={<ProtectedRoute><ChapterReview /></ProtectedRoute>} />
                      <Route path="/review/result" element={<ProtectedRoute><ReviewResult /></ProtectedRoute>} />
                    </Routes>
                  </div>
                  </CentralLoadingGate>
                </BrowserRouter>
              )}
            </ThemeProvider>
          </GoalModalProvider>
        </AvatarProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
